import React, { useState, useMemo, useCallback } from "react";
import { usePageBuilder } from "../../context/PageBuilderContext";
import { IComponentNode } from "../../types/pageBuilder.types";
import { FaChevronRight, FaChevronDown, FaSquare, FaHeading, FaFont, FaImage, FaLink, FaTh, FaList, FaVideo, FaIcons, FaCode, FaQuoteLeft, FaGripVertical } from "react-icons/fa";
import { useSortable } from "@dnd-kit/sortable";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface LayerTreeItemProps {
 component: IComponentNode;
 depth: number;
 isSelected: boolean;
 onSelect: (component: IComponentNode) => void;
 selectedComponent?: IComponentNode | null;
}

const getComponentIcon = (type: string) => {
 switch (type) {
  case "container":
   return <FaSquare className="text-blue-500" />;
  case "heading":
   return <FaHeading className="text-purple-500" />;
  case "text":
   return <FaFont className="text-gray-500" />;
  case "image":
   return <FaImage className="text-green-500" />;
  case "button":
   return <FaSquare className="text-indigo-500" />;
  case "link":
   return <FaLink className="text-blue-500" />;
  case "grid":
   return <FaTh className="text-orange-500" />;
  case "list":
   return <FaList className="text-teal-500" />;
  case "video":
   return <FaVideo className="text-red-500" />;
  case "icon":
   return <FaIcons className="text-pink-500" />;
  case "code":
   return <FaCode className="text-gray-600" />;
  case "quote":
   return <FaQuoteLeft className="text-yellow-500" />;
  default:
   return <FaSquare className="text-gray-400" />;
 }
};

const getComponentLabel = (component: IComponentNode): string => {
 if (component.type === "heading" && component.props?.text) {
  return `Heading: ${component.props.text.substring(0, 30)}${component.props.text.length > 30 ? "..." : ""}`;
 }
 if (component.type === "text" && component.props?.text) {
  return `Text: ${component.props.text.substring(0, 30)}${component.props.text.length > 30 ? "..." : ""}`;
 }
 if (component.type === "image" && component.props?.alt) {
  return `Image: ${component.props.alt}`;
 }
 if (component.type === "icon" && component.props?.iconName) {
  return `Icon: ${component.props.iconName}`;
 }
 if (component.type === "container" && component.props?.className) {
  const classes = component.props.className.split(" ").slice(0, 2).join(" ");
  return `Container: ${classes || "default"}`;
 }
 return `${component.type.charAt(0).toUpperCase() + component.type.slice(1)}`;
};

const LayerTreeItem: React.FC<LayerTreeItemProps> = ({ component, depth, isSelected, onSelect, selectedComponent }) => {
 // Initialize expanded state: auto-expand first 2 levels, or if this component contains the selected component
 // Calculate initial expanded state based on depth and selected component
 const calculateInitialExpanded = useCallback(() => {
  if (depth < 2) return true;
  // If selectedComponent exists and is a child of this component, start expanded
  if (selectedComponent && component.children) {
   const hasSelectedChild = component.children.some(child => {
    if (child.id === selectedComponent.id) return true;
    const checkChildren = (c: IComponentNode): boolean => {
     if (c.id === selectedComponent.id) return true;
     if (c.children) return c.children.some(ch => checkChildren(ch));
     return false;
    };
    return checkChildren(child);
   });
   return hasSelectedChild;
  }
  return false;
 }, [depth, selectedComponent, component.children]);
 const [isExpanded, setIsExpanded] = useState(() => calculateInitialExpanded());
 
 // Update expanded state when selectedComponent changes (for remounts)
 React.useEffect(() => {
  const shouldBeExpanded = calculateInitialExpanded();
  if (shouldBeExpanded && !isExpanded) {
   setIsExpanded(true);
  }
 }, [selectedComponent, component.children, depth, calculateInitialExpanded, isExpanded]);
 const hasChildren = component.children && component.children.length > 0;
 const icon = getComponentIcon(component.type);
 const label = getComponentLabel(component);

 // Auto-expand if this component contains the selected component (so moved components are visible)
 // This runs on mount and whenever selectedComponent changes to ensure moved components are visible
 React.useEffect(() => {
  if (selectedComponent && hasChildren && component.children) {
   const hasSelectedChild = component.children.some(child => {
    if (child.id === selectedComponent.id) return true;
    const checkChildren = (c: IComponentNode): boolean => {
     if (c.id === selectedComponent.id) return true;
     if (c.children) return c.children.some(ch => checkChildren(ch));
     return false;
    };
    return checkChildren(child);
   });
   if (hasSelectedChild && !isExpanded) {
    setIsExpanded(true);
   }
  }
 }, [selectedComponent, component.children, hasChildren, isExpanded, depth]);

 // Make items draggable for reordering
 const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
  id: component.id,
 });

 const style: React.CSSProperties = {
  transform: CSS.Transform.toString(transform),
  transition,
  opacity: isDragging ? 0.5 : 1,
 };

 // Note: We don't use nested SortableContext here - the root SortableContext in LayerTree
 // handles all components to avoid conflicts with dnd-kit

 return (
  <div>
   <div
    ref={setNodeRef}
    style={{ ...style, paddingLeft: `${depth * 16 + 8}px` }}
    className={`flex items-center gap-1 px-2 py-1.5 text-sm hover:bg-gray-100 rounded ${
     isSelected ? "bg-blue-100 border-l-2 border-blue-500" : ""
    } ${isDragging ? "z-50 shadow-lg" : ""}`}
   >
    {/* Drag handle */}
    <div
     {...attributes}
     {...listeners}
     className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0 p-0.5"
     title="Drag to reorder"
     onClick={(e) => {
      e.stopPropagation();
     }}
    >
     <FaGripVertical className="text-xs" />
    </div>
    
    {/* Expand/collapse button */}
    {hasChildren ? (
     <button
      type="button"
      onClick={(e) => {
       e.preventDefault();
       e.stopPropagation();
       setIsExpanded(!isExpanded);
      }}
      className="p-0.5 hover:bg-gray-200 rounded flex-shrink-0"
     >
      {isExpanded ? (
       <FaChevronDown className="text-xs text-gray-500" />
      ) : (
       <FaChevronRight className="text-xs text-gray-500" />
      )}
     </button>
    ) : (
     <span className="w-4 flex-shrink-0" />
    )}
    
    {/* Component icon and label - clickable area */}
    <div
     className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer"
     onClick={(e) => {
      e.stopPropagation();
      onSelect(component);
     }}
     onDoubleClick={(e) => {
      e.stopPropagation();
      onSelect(component);
      // Trigger drawer to open on double click
      window.dispatchEvent(new CustomEvent('pagebuilder:openDrawer'));
     }}
    >
     <span className="flex-shrink-0">{icon}</span>
     <span className="flex-1 truncate text-gray-700">{label}</span>
     {isSelected && (
      <span className="text-xs text-blue-600 font-medium flex-shrink-0">●</span>
     )}
    </div>
   </div>
   {hasChildren && isExpanded && (
    <div key={`children-${component.id}-${component.children!.map(c => c.id).join('-')}`}>
     {component.children!.map((child) => {
      // Use stable key (component ID) - React will detect order changes through the children array
      // The children container div has a key based on the order, which forces re-render when order changes
      return (
       <LayerTreeItem
        key={child.id}
        component={child}
        depth={depth + 1}
        isSelected={selectedComponent?.id === child.id}
        onSelect={onSelect}
        selectedComponent={selectedComponent}
       />
      );
     })}
    </div>
   )}
  </div>
 );
};

export const LayerTree: React.FC = () => {
 const { layout, selectedComponent, setSelectedComponent } = usePageBuilder();

 // Get all component IDs for SortableContext (flatten the tree)
 // This preserves the order of siblings, so reordering will change the result
 const getAllComponentIds = useCallback((node: IComponentNode | null): string[] => {
  if (!node) return [];
  const ids = [node.id];
  if (node.children) {
   node.children.forEach(child => {
    ids.push(...getAllComponentIds(child));
   });
  }
  return ids;
 }, []);

 // Hooks must be called before any early returns
 const allComponentIds = useMemo(() => {
  if (!layout) return [];
  return getAllComponentIds(layout);
 }, [layout, getAllComponentIds]);

 const handleSelect = (component: IComponentNode) => {
  // Single click: Just select the component, don't open drawer
  setSelectedComponent(component);
 };

 if (!layout) {
  return (
   <div className="p-4">
    <p className="text-sm text-gray-500">No layout to display</p>
   </div>
  );
 }

 // Use a stable key for the container to avoid unnecessary remounts
 // The SortableContext will handle updates when allComponentIds changes
 return (
  <div className="h-full flex flex-col">
   <div className="p-3 border-b border-gray-200 bg-gray-50">
    <h3 className="text-sm font-semibold text-gray-700">Layers</h3>
    <p className="text-xs text-gray-500 mt-1">Drag the grip icon to reorder elements</p>
   </div>
   <div className="flex-1 overflow-y-auto p-2">
    <SortableContext 
     items={allComponentIds} 
     strategy={verticalListSortingStrategy}
    >
     <LayerTreeItem
      key={layout.id}
      component={layout}
      depth={0}
      isSelected={selectedComponent?.id === layout.id}
      onSelect={handleSelect}
      selectedComponent={selectedComponent}
     />
    </SortableContext>
   </div>
  </div>
 );
};

